import fieldValidator from '@/utilities/fieldValidator';

// RFC 5322 "specials" that cannot appear in an unquoted address. Each one is a way to smuggle a
// second recipient past fieldValidator, whose email regex is only `/^.+@.+(\.).+/im` — a comma or
// semicolon turns one stored value into an address list.
const FORBIDDEN_SPECIALS = new Set([',', ';', '<', '>', '(', ')', '[', ']', '\\', '"']);

// fieldValidator's regex also uses the `m` flag, so "a@x.com\nBcc: evil@y.com" passes it — `^`
// matches after the newline. Anywhere these addresses become CC recipients (SendGrid, for one) that
// is a second recipient, so reject first. The control check is written as a code-point comparison,
// not a regex: a class of control escapes trips no-control-regex, and silencing that rule would
// hide exactly what this guard exists to catch.
const hasUnsafeChar = (email) => {
    for (const char of email) {
        if (FORBIDDEN_SPECIALS.has(char)) return true;

        const code = char.codePointAt(0);
        // Everything up to and including space, plus DEL. Non-ASCII is left alone, matching how
        // permissive fieldValidator already is.
        if (code <= 0x20 || code === 0x7f) return true;
    }

    return false;
};

/**
 * A single address, validated strictly enough to store and send to. Prefer this over
 * `fieldValidator(value, 'email')` for anything that will be emailed: fieldValidator alone accepts
 * address lists and header injection.
 *
 * Deliberately does not trim — the string checked is the string the caller has. Trim before calling
 * if the value comes straight from an input, so the value you store is the value that was validated.
 */
export const isValidEmail = (email) =>
    typeof email === 'string' && !!email && !hasUnsafeChar(email) && fieldValidator(email, 'email');

/**
 * Trims, drops blanks, dedupes case-insensitively (keeping the casing first seen), and splits the
 * rest into valid and invalid. Invalid addresses are returned rather than dropped so callers can
 * tell the user which ones failed.
 */
export const normalizeEmails = (input) => {
    // Without this, `for...of` over the string 'a@x.com' reports seven invalid addresses.
    if (!Array.isArray(input)) return { emails: [], invalid: [] };

    const seen = new Set();
    const emails = [];
    const invalid = [];

    for (const entry of input) {
        const email = typeof entry === 'string' ? entry.trim() : '';
        if (!email) continue;

        const key = email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        (isValidEmail(email) ? emails : invalid).push(email);
    }

    return { emails, invalid };
};
